/**
 * Base API Service Class
 * Provides common functionality for all API services including:
 * - Axios instance configuration
 * - Request/response interceptors
 * - Error handling
 * - Retry logic
 * - Status monitoring
 */
import axios from 'axios';
import { 
  addAuthHeader, 
  handleApiResponse, 
  createApiError, 
  createRetryFunction,
  isServiceOnline,
  extractRateLimitInfo 
} from '../../utils/apiHelpers';
import { appConfig } from '../../config/environment';

export default class BaseApiService {
  constructor(serviceName, baseURL, options = {}) {
    this.serviceName = serviceName;
    this.baseURL = baseURL;
    this.isOnline = false;
    this.lastChecked = null;
    this.rateLimitInfo = null;
    
    // Create axios instance
    this.client = axios.create({
      baseURL,
      timeout: options.timeout || appConfig.timeouts.api,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    this.setupInterceptors();
    
    console.log(`[${this.serviceName}] Service initialized with base URL: ${baseURL}`);
  }
  
  /**
   * Setup request and response interceptors
   */
  setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const startTime = Date.now();
        config.metadata = { startTime };
        
        // Add authentication header if needed
        config = addAuthHeader(config);
        
        const dataSize = config.data ? JSON.stringify(config.data).length : 0;
        console.log(`[${this.serviceName}] ${config.method?.toUpperCase()} ${config.url} - Data: ${dataSize}B`);
        
        return config;
      },
      (error) => {
        console.error(`[${this.serviceName}] Request interceptor error:`, error.message);
        return Promise.reject(error);
      }
    );
    
    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        const duration = response.config.metadata ? Date.now() - response.config.metadata.startTime : 0;
        const responseSize = response.data ? JSON.stringify(response.data).length : 0;
        
        console.log(`[${this.serviceName}] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status} in ${duration}ms, Response: ${responseSize}B`);
        
        // Extract rate limit info
        this.rateLimitInfo = extractRateLimitInfo(response.headers);
        this.isOnline = true;
        this.lastChecked = new Date();
        
        return handleApiResponse(response);
      },
      (error) => {
        const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : 0;
        const status = error.response?.status || 'Network Error';
        
        console.error(`[${this.serviceName}] ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${status} after ${duration}ms:`, error.message);
        
        // Update service status based on error type
        this.isOnline = isServiceOnline(error);
        this.lastChecked = new Date();
        
        // Extract rate limit info even from error responses
        if (error.response?.headers) {
          this.rateLimitInfo = extractRateLimitInfo(error.response.headers);
        }
        
        // Return standardized error response
        const apiError = createApiError(error, this.serviceName);
        return Promise.reject(apiError);
      }
    );
  }
  
  /**
   * Generic request method
   */
  async request(method, endpoint, data = null, config = {}) {
    try {
      const requestConfig = {
        method,
        url: endpoint,
        ...config
      };
      
      if (data) {
        if (method.toLowerCase() === 'get') {
          requestConfig.params = data;
        } else {
          requestConfig.data = data;
        }
      }
      
      const response = await this.client.request(requestConfig);
      return response;
      
    } catch (error) {
      // Re-throw the standardized error from interceptor
      throw error;
    }
  }
  
  /**
   * GET request
   */
  async get(endpoint, params = null, config = {}) {
    return this.request('GET', endpoint, params, config);
  }
  
  /**
   * POST request
   */
  async post(endpoint, data = null, config = {}) {
    return this.request('POST', endpoint, data, config);
  }
  
  /**
   * PUT request
   */
  async put(endpoint, data = null, config = {}) {
    return this.request('PUT', endpoint, data, config);
  }
  
  /**
   * DELETE request
   */
  async delete(endpoint, config = {}) {
    return this.request('DELETE', endpoint, null, config);
  }
  
  
  /**
   * Get current service status
   */
  getStatus() {
    return {
      serviceName: this.serviceName,
      isOnline: this.isOnline,
      lastChecked: this.lastChecked,
      rateLimitInfo: this.rateLimitInfo,
      baseURL: this.baseURL
    };
  }
  
  /**
   * Create retry-enabled version of a method
   */
  withRetry(method, maxRetries = appConfig.retry.attempts) {
    return createRetryFunction(method.bind(this), this.serviceName, maxRetries);
  }
}